/**
 * Artwork-Prüfung: das Prüfergebnis eines Artworks gegen JTL (EAN, Menge, Artikelnummer), Spec
 * `docs/superpowers/specs/2026-10-23-artwork-abgleich-design.md`. Gegenstück der Routen in
 * `backend/src/http/routes/artworkPruefungen.ts`:
 *
 * - `GET  /api/orders/:orderId/artwork-pruefungen`                  -> `{ pruefungen }`, ein Eintrag je Artwork der Bestellung
 * - `POST /api/orders/:orderId/artwork-pruefungen/:linkId/pruefen`  -> `{ pruefung }` (202), reiht die Prüfung neu ein
 * - `POST /api/orders/:orderId/artwork-pruefungen/:linkId/gesehen`  -> `{ pruefung }`, nimmt die Warnung aus dem Board
 *
 * Eigenes Modul statt Teil von `dokumente.ts`: die Antworten sind eingepackt, und das Polling des Reiters braucht den
 * Hintergrund-Kennzeichen-Weg (`AnfrageOptionen.hintergrund`), den die übrigen Positionsdokument-Aufrufe nicht kennen.
 */
import { anfrage } from "./basis.ts";

/** Gesamtergebnis einer fertigen Prüfung. `nicht_pruefbar` hat immer einen `grund`. */
export type ArtworkGesamt = "passt" | "warnung" | "selbst_pruefen" | "nicht_pruefbar";

/**
 * Ein Prüfpunkt. `punkt` ist einer von `produkt_ean`, `ve_ean`, `ve_menge`, `ue_ean`, `ue_menge`, `artikelnummer`,
 * `fremde_ean` (`Punktart` im Backend, `artworkPruefung/typen.ts`); der Server liefert sie in dieser Reihenfolge. Bei den
 * EAN-Punkten sind `soll` und `ist` 14-stellige GTINs mit führender Null (die Anzeige kürzt sie auf die Schreibweise des
 * Etiketts), bei den Mengen Zahlen als Text, bei der Artikelnummer der Text. Hat eine Rolle mehrere Gebinde (VE und VE
 * Neu), ist `soll` bei `ve_ean`/`ue_ean` die mit „, “ verbundene Liste aller GTINs der Rolle (`ist` nennt die gefundenen) und
 * bei `ve_menge`/`ue_menge` die Liste der Mengen; welche Menge zu welcher EAN gehört, steht dann im `hinweis`
 * („je EAN: 4068697000099 → 4, 4068697000105 → 6“). `ist` ist immer maschinell gelesen, `seiten` zählen ab 1. Bewusst
 * `string` statt einer Union: ein neuer Punkt im Backend soll die Anzeige nicht zum Absturz bringen (die Einzelheiten zeigen
 * ihn als Rohzeile).
 */
export interface ArtworkPruefpunkt {
  punkt: string;
  wert: "passt" | "warnung" | "selbst_pruefen";
  soll: string | null;
  ist: string[];
  seiten: number[];
  hinweis: string | null;
}

export interface ArtworkPruefung {
  linkId: string;
  jtlPosId: number;
  version: number;
  zustand: "nicht_geprueft" | "laeuft" | "fertig";
  gesamt: ArtworkGesamt | null;
  /**
   * Nur bei `gesamt "nicht_pruefbar"`: `format`, `defekt`, `kein_barcode`, `position_fehlt`, `ohne_artikel`, `grenze` oder
   * `technischer_fehler` (`NichtPruefbarGrund` im Backend). Bewusst `string`, siehe `ArtworkPruefpunkt`.
   */
  grund: string | null;
  ergebnis: {
    artikel: { kArtikel: number; artNr: string } | null;
    punkte: ArtworkPruefpunkt[];
    jtlHinweise: string[];
  } | null;
  /** ISO-Zeitpunkt der Prüfung; `null`, solange nichts geprüft wurde. */
  geprueftAm: string | null;
  gesehenVon: string | null;
  /** ISO-Zeitpunkt; gesetzt nur, wenn jemand die Warnung dieser Version als gesehen markiert hat. */
  gesehenAm: string | null;
}

export interface ArtworkPruefungenOptionen {
  signal?: AbortSignal;
  /** `true` für das Polling: die Anfrage zählt nicht als Handlung des Menschen (`AnfrageOptionen.hintergrund`). */
  hintergrund?: boolean;
}

const pfad = (orderId: string): string => `/api/orders/${encodeURIComponent(orderId)}/artwork-pruefungen`;

/** Der Prüfstand aller Artworks der Bestellung, auch der noch nicht geprüften (`zustand "nicht_geprueft"`). */
export async function artworkPruefungenLaden(
  orderId: string,
  optionen: ArtworkPruefungenOptionen = {},
): Promise<ArtworkPruefung[]> {
  const antwort = await anfrage<{ pruefungen: ArtworkPruefung[] }>(pfad(orderId), {
    signal: optionen.signal,
    hintergrund: optionen.hintergrund,
  });
  return antwort.pruefungen;
}

/** „Prüfen“ / „Erneut prüfen“: reiht die Prüfung des Artworks neu ein. Recht `manage_documents`. */
export async function artworkPruefungAnstossen(orderId: string, linkId: string): Promise<ArtworkPruefung> {
  const antwort = await anfrage<{ pruefung: ArtworkPruefung }>(
    `${pfad(orderId)}/${encodeURIComponent(linkId)}/pruefen`,
    { methode: "POST" },
  );
  return antwort.pruefung;
}

/**
 * „Gesehen“: vermerkt, dass jemand die Warnung dieser Version gesehen hat; das Board zählt sie dann nicht mehr. 409, wenn
 * keine offene Warnung vorliegt; Recht `manage_documents`.
 */
export async function artworkPruefungGesehen(orderId: string, linkId: string): Promise<ArtworkPruefung> {
  const antwort = await anfrage<{ pruefung: ArtworkPruefung }>(
    `${pfad(orderId)}/${encodeURIComponent(linkId)}/gesehen`,
    { methode: "POST" },
  );
  return antwort.pruefung;
}
